import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { Invoice } from '../types';

// Create styles for PDF
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 20,
    borderBottom: '2 solid #2c3e50',
    paddingBottom: 10,
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 5,
  },
  companyInfo: {
    fontSize: 9,
    color: '#7f8c8d',
    marginBottom: 2,
  },
  invoiceTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginTop: 20,
    marginBottom: 10,
  },
  sectionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  section: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 5,
    textTransform: 'uppercase',
  },
  sectionContent: {
    fontSize: 10,
    color: '#2c3e50',
    marginBottom: 2,
  },
  table: {
    marginTop: 20,
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#34495e',
    color: '#ffffff',
    padding: 8,
    fontWeight: 'bold',
    fontSize: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #ecf0f1',
    padding: 8,
    fontSize: 10,
  },
  tableRowAlt: {
    flexDirection: 'row',
    backgroundColor: '#f8f9fa',
    borderBottom: '1 solid #ecf0f1',
    padding: 8,
    fontSize: 10,
  },
  col1: {
    width: '50%',
  },
  col2: {
    width: '15%',
    textAlign: 'right',
  },
  col3: {
    width: '15%',
    textAlign: 'right',
  },
  col4: {
    width: '20%',
    textAlign: 'right',
  },
  totalsSection: {
    marginTop: 20,
    marginLeft: 'auto',
    width: '40%',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 5,
    fontSize: 10,
  },
  totalRowBold: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 5,
    fontSize: 12,
    fontWeight: 'bold',
    borderTop: '2 solid #2c3e50',
    marginTop: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    color: '#7f8c8d',
    fontSize: 9,
    borderTop: '1 solid #ecf0f1',
    paddingTop: 10,
  },
  statusBadge: {
    backgroundColor: '#3498db',
    color: '#ffffff',
    padding: '4 8',
    borderRadius: 3,
    fontSize: 9,
    alignSelf: 'flex-start',
  },
  notes: {
    marginTop: 20,
    padding: 10,
    backgroundColor: '#f8f9fa',
    borderRadius: 4,
  },
  notesTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  notesContent: {
    fontSize: 9,
    color: '#2c3e50',
  },
});

interface InvoicePDFProps {
  invoice: Invoice;
  companyInfo?: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    email: string;
    website?: string;
  };
}

const InvoicePDF: React.FC<InvoicePDFProps> = ({ invoice, companyInfo }) => {
  const defaultCompanyInfo = {
    name: 'Your Company Name',
    address: '123 Business St',
    city: 'San Francisco',
    state: 'CA',
    zip: '94102',
    phone: '(555) 123-4567',
    email: 'info@yourcompany.com',
    website: 'www.yourcompany.com',
  };

  const company = companyInfo || defaultCompanyInfo;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      draft: '#95a5a6',
      sent: '#3498db',
      paid: '#27ae60',
      overdue: '#e74c3c',
      cancelled: '#7f8c8d',
    };
    return colors[status] || '#95a5a6';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with Company Info */}
        <View style={styles.header}>
          <Text style={styles.companyName}>{company.name}</Text>
          <Text style={styles.companyInfo}>{company.address}</Text>
          <Text style={styles.companyInfo}>
            {company.city}, {company.state} {company.zip}
          </Text>
          <Text style={styles.companyInfo}>Phone: {company.phone}</Text>
          <Text style={styles.companyInfo}>Email: {company.email}</Text>
          {company.website && <Text style={styles.companyInfo}>Web: {company.website}</Text>}
        </View>

        {/* Invoice Title and Status */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text style={styles.invoiceTitle}>INVOICE</Text>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(invoice.status) }]}>
            <Text>{invoice.status.toUpperCase()}</Text>
          </View>
        </View>

        {/* Invoice Details and Bill To Section */}
        <View style={styles.sectionRow}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Bill To:</Text>
            <Text style={styles.sectionContent}>{invoice.customer_details?.name}</Text>
            {invoice.customer_details?.address_line1 && (
              <Text style={styles.sectionContent}>{invoice.customer_details.address_line1}</Text>
            )}
            {invoice.customer_details?.address_line2 && (
              <Text style={styles.sectionContent}>{invoice.customer_details.address_line2}</Text>
            )}
            <Text style={styles.sectionContent}>
              {invoice.customer_details?.city}, {invoice.customer_details?.state}{' '}
              {invoice.customer_details?.zip_code}
            </Text>
            {invoice.customer_details?.email && (
              <Text style={styles.sectionContent}>{invoice.customer_details.email}</Text>
            )}
            {invoice.customer_details?.phone && (
              <Text style={styles.sectionContent}>{invoice.customer_details.phone}</Text>
            )}
          </View>

          <View style={[styles.section, { alignItems: 'flex-end' }]}>
            <Text style={styles.sectionTitle}>Invoice Details:</Text>
            <Text style={styles.sectionContent}>Invoice #: {invoice.invoice_number}</Text>
            <Text style={styles.sectionContent}>PO #: {invoice.po_details?.po_number}</Text>
            <Text style={styles.sectionContent}>Issue Date: {formatDate(invoice.issue_date)}</Text>
            <Text style={styles.sectionContent}>Due Date: {formatDate(invoice.due_date)}</Text>
            <Text style={styles.sectionContent}>
              Payment Terms: {invoice.po_details?.payment_terms_display}
            </Text>
          </View>
        </View>

        {/* Line Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.col1}>Description</Text>
            <Text style={styles.col2}>Qty</Text>
            <Text style={styles.col3}>Rate</Text>
            <Text style={styles.col4}>Amount</Text>
          </View>

          {invoice.line_items.map((item, index) => (
            <View key={item.id || index} style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
              <Text style={styles.col1}>{item.description}</Text>
              <Text style={styles.col2}>{item.quantity}</Text>
              <Text style={styles.col3}>{formatCurrency(Number(item.unit_price))}</Text>
              <Text style={styles.col4}>{formatCurrency(item.total || 0)}</Text>
            </View>
          ))}
        </View>

        {/* Totals Section */}
        <View style={styles.totalsSection}>
          <View style={styles.totalRow}>
            <Text>Subtotal:</Text>
            <Text>{formatCurrency(invoice.subtotal || 0)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Tax ({invoice.tax_rate}%):</Text>
            <Text>{formatCurrency(invoice.tax_amount || 0)}</Text>
          </View>
          <View style={styles.totalRowBold}>
            <Text>Total Due:</Text>
            <Text>{formatCurrency(invoice.total_amount || 0)}</Text>
          </View>
        </View>

        {/* Notes Section */}
        {invoice.notes && (
          <View style={styles.notes}>
            <Text style={styles.notesTitle}>Notes:</Text>
            <Text style={styles.notesContent}>{invoice.notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Thank you for your business!</Text>
          <Text>
            Please remit payment by {formatDate(invoice.due_date)} to avoid late fees.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;


